import {
    useContext,
    createContext,
    useMemo,
    useEffect,
    useCallback,
    PropsWithChildren,
    ComponentType,
    useRef,
    DependencyList,
} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/dist/client/router';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '../redux/reducers';
import {authenticate, authenticateViaLoginless, login, logout, setAuthUser} from '../redux/actions/authActions';
import {AuthStatus, AuthUser, LoginFeedback} from '../redux/types/auth';
import {usePrevious} from './usePrevious';
import {LoadingState} from 'components/LoadingState';
import {usePageNavigation} from './usePageNavigation';
import {api} from './utils/api';

type AuthProps = {
    status: AuthStatus;
    isLoggingIn: boolean;
    userData: AuthUser;
    loginRequestFeedback: LoginFeedback | null;
    signIn: (email: string) => void;
    signOut: () => void;
    setUser: (userData: any) => void;
    authenticate: (vt?: string) => void;
};

const AuthContext = createContext<AuthProps>({} as AuthProps);

export function useAuthenticatedEffect<T extends (...args: unknown[]) => unknown>(
    effect: T,
    deps: DependencyList,
    once = false,
) {
    const status = useSelector((state: AppState) => state.auth.status);
    const previousStatus = usePrevious(status);
    const executed = useRef(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const callback = useCallback(effect, deps);

    useEffect(() => {
        if (once && executed.current) return;

        if (previousStatus && status === AuthStatus.AUTHENTICATED) {
            callback();
            executed.current = true;
        }
    }, [callback, once, status, previousStatus]);
}

export function useAuthenticatedEffectOnce<T extends (...args: unknown[]) => unknown>(effect: T, deps: DependencyList) {
    useAuthenticatedEffect(effect, deps, true);
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within a AuthProvider');
    }
    return context;
};

type Props = {
    Component: ComponentType & {
        authenticate?: boolean;
        redirectOnAuthenticated?: boolean;
        loginless?: boolean;
        loginlessScope?: string;
    };
    redirectUnauthenticatedTo: string;
};

function Loading() {
    return (
        <>
            <Head>
                <title>Amplify</title>
            </Head>
            <LoadingState sx={{minHeight: '100vh'}} />
        </>
    );
}

export function AuthProvider({children, Component, redirectUnauthenticatedTo}: PropsWithChildren<Props>) {
    const router = useRouter();
    const dispatch = useDispatch();
    const authState = useSelector((state: AppState) => state.auth);
    const {isFetched: userIsFetched} = api.user.get.useQuery(undefined, {
        enabled: authState.status === AuthStatus.AUTHENTICATED,
    });
    const {goToOTPVerification} = usePageNavigation();

    const signIn = useCallback((email: string) => {
        dispatch(login(email));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signOut = useCallback(async () => {
        dispatch(logout());
    }, []);

    const setUser = useCallback((user: any) => {
        dispatch(setAuthUser(user));
    }, []);

    const tryAuthenticate = useCallback((vt?: string) => dispatch(authenticate(vt)), []);

    const tryLoginlessAuthenticate = useCallback(
        (vt?: string, llt?: string, scope?: string) => dispatch(authenticateViaLoginless(vt, llt, scope)),
        [],
    );

    useEffect(() => {
        if (Component.loginless) {
            // tryLoginlessAuthenticate will attempt regular authentication but not yield UNAUTHENTICATED
            // until loginless authentication has been attempted aftwards
            tryLoginlessAuthenticate(router.query.vt as string, router.query.llt as string, Component.loginlessScope);
        } else if (Component.authenticate || Component.redirectOnAuthenticated) {
            // if query token exists verify
            // if access and refresh token expired, log out
            // if access token expired and refresh active, request new access
            // if access active, verify
            tryAuthenticate(router.query.vt as string);
        }
    }, [Component, router.pathname, router.query, tryAuthenticate, tryLoginlessAuthenticate]);

    useEffect(() => {
        if (
            (Component.authenticate || Component.loginless) &&
            (authState.status === AuthStatus.UNAUTHENTICATED || authState.status === AuthStatus.LOGOUT)
        ) {
            router.push(redirectUnauthenticatedTo);
            return;
        }

        if (authState.status !== AuthStatus.AUTHENTICATED) return;
        if (!userIsFetched) return;
        if (!Component.redirectOnAuthenticated) return;
        if (Component.loginless && Component.loginlessScope) {
            router.push(Component.loginlessScope);
            return;
        }
        goToOTPVerification();
    }, [Component, authState.status, router.query, redirectUnauthenticatedTo, userIsFetched]);

    const shouldShowLoading = useMemo(
        () =>
            // Shouldn't show loading if a user was previously authenticated
            (!authState.user.id &&
                !Component.loginless &&
                Component.authenticate &&
                authState.status !== AuthStatus.AUTHENTICATED) ||
            (Component.redirectOnAuthenticated &&
                authState.status !== AuthStatus.UNAUTHENTICATED &&
                authState.status !== AuthStatus.AUTHENTICATED_LOGINLESS) ||
            (Component.loginless && authState.status !== AuthStatus.AUTHENTICATED_LOGINLESS),
        [Component, authState.status, authState.user.id],
    );

    const auth = useMemo(
        () => ({
            status: authState.status,
            isLoggingIn: authState.isLoggingIn,
            userData: authState.user,
            loginRequestFeedback: authState.loginFeedback,
            setUser,
            signIn,
            signOut,
            authenticate: tryAuthenticate,
            loginlessAuthenticate: tryLoginlessAuthenticate,
        }),
        [authState],
    );

    return <AuthContext.Provider value={auth}>{shouldShowLoading ? <Loading /> : children}</AuthContext.Provider>;
}
